/**
 * First-run provisioning model (ADR 175). A freshly-cleared system has no
 * tenant/site/warehouse, so the shell must present a guided setup instead of a
 * full app rendered against nothing. This module is the pure core: given who
 * the user is and what exists, it derives the ordered setup steps. The shell
 * (App redirect, Sidebar gating, GettingStartedPage) reads live counts off the
 * store/queries and feeds them here.
 */

/**
 * `operator` (ADR 408): granted at least one site but able to provision nothing —
 * the viewer/supervisor persona. It is the opposite of `other`, which holds no
 * grant at all; the two used to share one bucket, and the setup page told a
 * granted viewer their account "has no access yet".
 */
export type SetupRole = "global-admin" | "site-admin" | "warehouse-admin" | "operator" | "other";

/** ROLE_RANK["site-admin"] in siteStore; kept local so this stays a pure leaf. */
const SITE_ADMIN_RANK = 2;

export interface SetupCounts {
  tenantCount: number;
  siteCount: number;
  warehouseCount: number;
}

export type SetupStepKey = "tenant" | "site" | "warehouse";

export interface SetupStep {
  key: SetupStepKey;
  done: boolean;
  /** The first not-yet-done step — the one to highlight and act on next. */
  current: boolean;
  /** Deep-link to the existing create flow for this step. */
  ctaTo: string;
}

const STEP_CTA: Record<SetupStepKey, string> = {
  tenant: "/admin/tenants",
  site: "/admin/sites",
  warehouse: "/admin/warehouses",
};

/**
 * Map identity to the setup persona — by **grant**, never by realm role alone
 * (ADR 223).
 *
 * The distinction is the whole point. A Keycloak realm role such as
 * `site-admin` is a capability label; it says what kind of thing an account may
 * do *once it has been given something to do it to*. Authorization is the grant
 * set (ADR 110: "a subject's access IS its resolved grant set"), and under
 * ADR 167 every site belongs to a tenant, so creating one requires an
 * `org_unit/admin` grant — a realm role can never substitute for it.
 *
 * Deriving the persona from `roleRank` alone therefore invited accounts to run
 * a checklist whose first step the backend would refuse: the sidebar (already
 * grant-derived, ADR 177/178) correctly showed nothing actionable while this
 * page said "create your first site". Each step below is now offered only to
 * someone who can actually complete it.
 */
export function setupRole(
  isGlobalAdmin: boolean,
  roleRank: number,
  canAdminOrgUnits: boolean,
  hasSite: boolean,
): SetupRole {
  if (isGlobalAdmin) return "global-admin";
  // An org-unit admin holds the grant POST /v1/sites checks, with or without the
  // site-admin realm role (ADR 177).
  if (canAdminOrgUnits) return "site-admin";
  // Realm role but no tenant grant: cannot create a site, but can still create a
  // warehouse inside a site already granted to them (assertWriteAccess = site in
  // scope AND the site-admin role). With no site in scope there is nothing to
  // provision at all.
  if (roleRank >= SITE_ADMIN_RANK && hasSite) return "warehouse-admin";
  // Granted a site, cannot create anything: nothing to set up, but plenty to
  // see. Only a grant-less account is "other" (ADR 408).
  if (hasSite) return "operator";
  return "other";
}

/** The ordered checklist for this role, each step flagged done/current with its CTA. */
export function setupSteps(role: SetupRole, counts: SetupCounts): SetupStep[] {
  const keys: SetupStepKey[] =
    role === "global-admin" ? ["tenant", "site", "warehouse"]
    : role === "site-admin" ? ["site", "warehouse"]
    : role === "warehouse-admin" ? ["warehouse"]
    : [];

  const isDone = (k: SetupStepKey): boolean =>
    k === "tenant" ? counts.tenantCount > 0
    : k === "site" ? counts.siteCount > 0
    : counts.warehouseCount > 0;

  const firstUndone = keys.find((k) => !isDone(k));
  return keys.map((k) => ({ key: k, done: isDone(k), current: k === firstUndone, ctaTo: STEP_CTA[k] }));
}

/** True only when the persona had steps and every one is done. An empty list
 *  (a non-provisioning role) is never "complete" — there is nothing to finish. */
export function setupComplete(steps: SetupStep[]): boolean {
  return steps.length > 0 && steps.every((s) => s.done);
}

/**
 * Whether `/` should land on the guided setup rather than the overview (ADR 318).
 *
 * "No warehouse" is only a provisioning verdict when the answer actually
 * arrived. A list that is still in flight, or one whose load failed, is *unknown*
 * — and unknown must resolve to the overview: an operating customer briefly
 * reading zero (an expired token, a dropped request) was being told their system
 * was never set up, which is both false and alarming. The overview's own widgets
 * report their own failures; the checklist cannot report anything but "empty".
 */
export function shouldShowSetup(
  hasWarehouse: boolean, warehousesResolved: boolean, warehousesError: boolean, role: SetupRole,
): boolean {
  // An operator has no checklist to land on; a site without a warehouse is the
  // overview's problem to describe, not the setup page's (ADR 408).
  if (role === "operator") return false;
  return !hasWarehouse && warehousesResolved && !warehousesError;
}
